import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";

const EMPTY_SUMMARY = {
  totalEmployees: 0,
  activeEmployees: 0,
  presentToday: 0,
  onLeaveToday: 0,
  pendingLeaves: 0,
  newJoinersThisMonth: 0,
};

function SummaryMetric({ label, value, tone }) {
  const accent =
    tone === "success" ? "var(--success)" :
    tone === "danger" ? "var(--danger)" :
    tone === "warn" ? "var(--warn)" :
    "#059669";

  return (
    <div className="summary-card" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="summary-card-label">{label}</div>
      <div className="summary-card-value" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const tone =
    status === "Pending" ? "warn" :
    status === "Approved" || status === "Active" ? "success" :
    status === "Rejected" ? "danger" :
    "neutral";

  return <span className={`hrms-badge ${tone}`}>{status}</span>;
}

export default function HrmsDashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryRes, employeesRes, leavesRes] = await Promise.all([
        apiFetch("/api/hrms/summary"),
        apiFetch("/api/hrms/employees"),
        apiFetch("/api/hrms/leaves?status=Pending"),
      ]);

      if (!summaryRes.ok) throw new Error(await summaryRes.text());
      if (!employeesRes.ok) throw new Error(await employeesRes.text());
      if (!leavesRes.ok) throw new Error(await leavesRes.text());

      setSummary(await summaryRes.json());
      setEmployees(await employeesRes.json());
      setLeaves(await leavesRes.json());
    } catch (err) {
      setError(err?.message || "Could not load HRMS dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recentEmployees = employees
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 5);

  return (
    <div className="hrms-page">
      <PageHeader
        title="HRMS Dashboard"
        subtitle="Employee strength, attendance, leave requests, and joining trends."
        actions={
          <button className="primary" onClick={() => navigate("/hrms/employees")}>
            Add Employee
          </button>
        }
      />

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <Loader.SummaryCards />
      ) : (
        <div className="summary-grid">
          <SummaryMetric label="Total Employees" value={summary.totalEmployees} />
          <SummaryMetric label="Active Employees" value={summary.activeEmployees} tone="success" />
          <SummaryMetric label="Present Today" value={summary.presentToday} tone="success" />
          <SummaryMetric label="On Leave Today" value={summary.onLeaveToday} tone="warn" />
          <SummaryMetric label="Pending Leaves" value={summary.pendingLeaves} tone="danger" />
          <SummaryMetric label="New Joiners" value={summary.newJoinersThisMonth} />
        </div>
      )}

      <div className="hrms-dashboard-grid">
        <section className="glass-card hrms-panel">
          <div className="hrms-panel-header">
            <div>
              <h3>Recent Employees</h3>
              <p>Latest employee records added to HRMS.</p>
            </div>
            <button className="btn-ghost" onClick={() => navigate("/hrms/employees")}>
              View All
            </button>
          </div>

          {loading ? (
            <div className="hrms-list-skeleton">
              {[1, 2, 3, 4].map((item) => (
                <Loader key={item} type="skeleton" height={42} />
              ))}
            </div>
          ) : recentEmployees.length === 0 ? (
            <EmptyState
              title="No employees yet"
              subtitle="Add employees to start using attendance and leave workflows."
            />
          ) : (
            <div className="hrms-compact-list">
              {recentEmployees.map((employee) => (
                <div key={employee.id} className="hrms-compact-row">
                  <div className="avatar">{employee.fullName?.[0]?.toUpperCase() || "E"}</div>
                  <div>
                    <div className="hrms-row-title">{employee.fullName}</div>
                    <div className="hrms-row-subtitle">
                      {employee.employeeCode} - {employee.designation || "Employee"}
                    </div>
                  </div>
                  <StatusBadge status={employee.status} />
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="glass-card hrms-panel">
          <div className="hrms-panel-header">
            <div>
              <h3>Pending Leave Requests</h3>
              <p>Requests waiting for HR approval.</p>
            </div>
            <button className="btn-ghost" onClick={() => navigate("/hrms/leaves")}>
              Review
            </button>
          </div>

          {loading ? (
            <div className="hrms-list-skeleton">
              {[1, 2, 3, 4].map((item) => (
                <Loader key={item} type="skeleton" height={42} />
              ))}
            </div>
          ) : leaves.length === 0 ? (
            <EmptyState
              title="No pending leaves"
              subtitle="Approved and rejected requests will stay available on the Leaves page."
            />
          ) : (
            <div className="hrms-compact-list">
              {leaves.slice(0, 5).map((leave) => (
                <div key={leave.id} className="hrms-compact-row">
                  <div>
                    <div className="hrms-row-title">{leave.employeeName}</div>
                    <div className="hrms-row-subtitle">
                      {leave.leaveType} - {formatDate(leave.startDate)} to {formatDate(leave.endDate)}
                    </div>
                  </div>
                  <StatusBadge status={leave.status} />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
